import {
  MY_THERAPY_PAGES,
  PAGE_TYPE_ACCOUNT,
  PAGE_TYPE_CART,
  PAGE_TYPE_CHECKOUT,
  PAGE_TYPE_LOGIN,
  PAGE_TYPE_MOBILE_APP,
  PAGE_TYPE_NEWSLETTER,
} from 'shared/consts'
import logger from 'shared/services/logger'
import store from 'shared/store'
import { SPA_CART } from 'views/consts'

type UpdateCountdownArgs = {
  days: string
  hours: string
  minutes: string
  seconds: string
}

const ONE_SECOND = 1000
const ONE_MINUTE = ONE_SECOND * 60
const ONE_HOUR = ONE_MINUTE * 60
const ONE_DAY = ONE_HOUR * 24
const BANNER_CLASS_DEBOUNCE_MS = 100

const updateCountdownElement = ({
  days,
  hours,
  minutes,
  seconds,
}: UpdateCountdownArgs) => {
  const fields = ['days', 'hours', 'minutes', 'seconds']
  const values = [days, hours, minutes, seconds]

  fields.forEach(field => {
    const element = document.querySelector(
      `[data-clientside-hook~="stickyBanner-${field}"]`
    )

    if (days === '0' && field === 'days' && element) {
      element?.parentElement?.remove()
    }

    if (element) {
      element.textContent = values[fields.indexOf(field)].padStart(2, '0')
    }
  })
}

// calculates and returns the countdown
const calculateAndGetCountdown = (date: Date) => {
  const now = new Date()
  const diffInMiliSeconds = date.getTime() - now.getTime()

  if (diffInMiliSeconds < 0) {
    return {
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
      done: true,
    }
  }

  // get the remaining hours,minutes,seconds as miliseconds
  // and convert them to human-readable strings
  const days = Math.floor(diffInMiliSeconds / ONE_DAY).toString()
  const hours = Math.floor((diffInMiliSeconds % ONE_DAY) / ONE_HOUR).toString()
  const minutes = Math.floor(
    (diffInMiliSeconds % ONE_HOUR) / ONE_MINUTE
  ).toString()
  const seconds = Math.floor(
    (diffInMiliSeconds % ONE_MINUTE) / ONE_SECOND
  ).toString()

  return {
    days,
    hours,
    minutes,
    seconds,
    done: false,
  }
}

const calculateCountdownAndUpdate = (date: Date) => {
  const countdown = calculateAndGetCountdown(date)

  updateCountdownElement(countdown)

  if (!countdown.done) {
    setTimeout(() => {
      calculateCountdownAndUpdate(date)
    }, 1000)
  }
}

const isBannerVisible = (selector: string) => {
  const element = document.querySelector(selector)
  return element && !element.classList.contains('u-hidden')
}

const isStickyBannerVisible = () =>
  isBannerVisible('[data-clientside-hook~="stickyBanner"]')

const isStickyCtaVisible = () =>
  isBannerVisible('[data-clientside-hook="sticky-cta"]')

// Debounced function to manage withNlBanner class
export const manageBannerClass = (() => {
  let bannerClassTimeout: NodeJS.Timeout

  return () => {
    clearTimeout(bannerClassTimeout)
    bannerClassTimeout = setTimeout(() => {
      const shouldHaveClass = isStickyBannerVisible() || isStickyCtaVisible()
      if (shouldHaveClass) {
        document.body.classList.add('withNlBanner')
      } else {
        document.body.classList.remove('withNlBanner')
      }
    }, BANNER_CLASS_DEBOUNCE_MS)
  }
})()

const handleVoucherCopy = () => {
  const stickyBannerCopyVoucher = document.querySelector(
    '.o-StickyBanner-voucherCode'
  )
  stickyBannerCopyVoucher?.addEventListener('click', async () => {
    const voucherCodeButton = document.querySelector(
      '[data-clientside-hook~="stickyBannerVoucherCopyButton"]'
    )
    const voucherCode = document.querySelector(
      '.o-StickyBanner-voucherCode'
    ) as HTMLElement

    if (voucherCodeButton && voucherCode) {
      await navigator.clipboard.writeText(
        voucherCode?.dataset.voucherCode as string
      )
      voucherCode.textContent = voucherCode.dataset.textCopied as string
      voucherCodeButton?.classList.add('o-StickyBanner-voucherCode--copied')

      setTimeout(() => {
        voucherCodeButton.classList.remove('o-StickyBanner-voucherCode--copied')
        voucherCode.textContent = voucherCode.dataset.voucherCode as string
      }, 2000)
    }
  })
}

const PAGES_TO_DISABLE_STICKY_BANNER = [
  PAGE_TYPE_CHECKOUT,
  PAGE_TYPE_ACCOUNT,
  PAGE_TYPE_LOGIN,
  PAGE_TYPE_NEWSLETTER,
  PAGE_TYPE_MOBILE_APP,
  ...MY_THERAPY_PAGES,
]

type stickyBannerOptions = {
  id: string
  type: 'countdown' | 'generic'
  startDate: string
  endDate: string
  blackListedUrls: string
  whiteListedUrls: string
}

export default () => {
  const stickyBanner = document.querySelector(
    '[data-clientside-hook~="stickyBanner"]'
  )
  const stickyBannerClose = document.querySelector(
    '[data-clientside-hook~="stickyBannerClose"]'
  )

  const { pageType } = store.getPublicRuntimeConfig().pageProperties

  if (
    stickyBanner &&
    stickyBanner instanceof HTMLElement &&
    stickyBanner.dataset.options &&
    stickyBannerClose &&
    !PAGES_TO_DISABLE_STICKY_BANNER.includes(pageType)
  ) {
    try {
      const options = JSON.parse(
        stickyBanner.dataset.options
      ) as stickyBannerOptions
      const isStartDatePassed = new Date(options.startDate) < new Date()
      const isEndDatePassed = new Date(options.endDate) < new Date()
      const blackList = options.blackListedUrls.split(',')
      const isBlacklisted =
        blackList[0] &&
        blackList.filter(url => window.location.href.includes(url)).length

      //if the current page URL is blacklisted, we don't show the banner
      if (isBlacklisted) {
        return
      }

      const whiteList = options.whiteListedUrls.split(',')
      const pageNotInTheWhiteList =
        whiteList[0] &&
        !whiteList.filter(url => window.location.href.includes(url)).length

      //if the whitelist exists, but the current page URL is not there, we don't show the banner
      if (pageNotInTheWhiteList) {
        return
      }

      if (
        isStartDatePassed &&
        !isEndDatePassed &&
        !window.sessionStorage.getItem(options.id)
      ) {
        if (options.type === 'countdown') {
          calculateCountdownAndUpdate(new Date(options.endDate))
          handleVoucherCopy()
        }
        // make the banner visible
        stickyBanner.classList.remove('u-hidden')
        document.body.classList.add('withNlBanner')
        // Ensure banner class is properly managed
        manageBannerClass()

        const cart = store.getState().cart.data
        const spaCart = document.getElementById(SPA_CART)
        let observer: IntersectionObserver | null = null
        if (pageType === PAGE_TYPE_CART && spaCart && cart.sellerGroups) {
          observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
              stickyBanner.classList.remove('u-hidden')
            } else {
              stickyBanner.classList.add('u-hidden')
            }
          })
          observer.observe(spaCart)
        }
        // handle close button click
        stickyBannerClose.addEventListener(
          'click',
          () => {
            // Clean up the observer before removing the banner
            if (observer) {
              observer.disconnect()
              observer = null
            }
            stickyBanner.remove()
            window.sessionStorage.setItem(options.id, 'true')
            // Use debounced function to manage banner class
            manageBannerClass()
          },
          {
            once: true,
          }
        )
      }
    } catch (e) {
      logger.error(e)
    }
  }
}
